// src/store/modules/userActions.js
import {
  doc,
  getDoc,
  getDocs,
  collection,
  updateDoc,
} from "@/services/legacyBrowserPersistenceBoundary";
import { auth } from "@/firebase/authorityClient";
import {
  isOwnWalletDetailFeatureEnabled,
  isWalletStatementV3FeatureEnabled,
} from "@/config/runtimeConfig";
import { walletDetailAuthorityService } from "@/services/walletDetailAuthorityService";
import { walletSummaryService } from "@/services/walletSummaryService";
import { walletStatementEntryKey } from "@/utils/walletStatement";
import { assertLegacyMonetaryClientAvailable } from "@/services/legacyMonetaryBoundary";

let activeWalletUnsubscribe = null;
let activeWalletSubscriptionKey = null;
let activeWalletGeneration = 0;
let privateWalletDetailGeneration = 0;

const MAX_PRIVATE_WALLET_STATEMENT_PAGES = 10;

const rootCredentialUid = (rootGetters) =>
  rootGetters.userCredentials?.data?.uid ||
  rootGetters.userCredentials?.uid ||
  null;

const ownsPrivateWalletContext = ({ actor, rootGetters, uid, orgUID }) =>
  auth.currentUser === actor &&
  actor?.uid === uid &&
  rootCredentialUid(rootGetters) === uid &&
  rootGetters.selectedOrgUID === orgUID &&
  Array.isArray(rootGetters.userOrgIds) &&
  rootGetters.userOrgIds.includes(orgUID);

// Request version 3 returns the combined statement as `entries`; version 1
// returns Current settlements as `statement`.
const statementRowsProperty = (detail) =>
  detail?.schemaVersion === 3 ? "entries" : "statement";
const statementRowsOf = (detail) =>
  detail?.[statementRowsProperty(detail)] || [];
const statementRowKey = (row) =>
  typeof row.settlementRef === "string"
    ? row.settlementRef
    : walletStatementEntryKey(row);

const privateWalletErrorCode = (error) => {
  const code = String(error?.code || "unknown").split("/").pop();
  return [
    "deadline-exceeded",
    "failed-precondition",
    "invalid-argument",
    "invalid-response",
    "permission-denied",
    "stale-auth-context",
    "ui-deadline",
    "unauthenticated",
    "unavailable",
  ].includes(code)
    ? code
    : "unknown";
};

export default {
  state: {
    otherUserData: null,
    orgDetails: {
      totalOrgCoins: 0,
    },
    coinName: null,
    coinSymbol: null,
    buyerOrgUID: null,
    sellerOrgUID: null,
    buyerOrgDetails: {},
    sellerOrgDetails: {},
    currentUserOrgUID: null,
    currentUserWallet: null,
    currentUserWalletOwnerUID: null,
    currentUserWalletStatus: "idle",
    privateWalletDetail: null,
    privateWalletDetailOwnerUID: null,
    privateWalletDetailOrgUID: null,
    privateWalletDetailStatus: "idle",
    privateWalletDetailError: null,
    privateWalletDetailRequestGeneration: 0,
    privateWalletDetailRefreshing: false,
    privateWalletDetailStale: false,
    privateWalletStatementCursor: null,
    privateWalletStatementHasMore: false,
    privateWalletStatementLoadingMore: false,
    privateWalletStatementError: null,
    privateWalletStatementPagesLoaded: 0,
    privateWalletStatementSeenCursors: [],
    privateWalletStatementTruncated: false,
    walletBalanceRevision: 0,
    profileUserData: null,
    profileUserOrgDetails: {},
  },
  getters: {
    otherUserData: (state) => state.otherUserData,
    coinSymbol: (state) => state.coinSymbol,
    getBuyerOrgDetails: (state) => (orgUID) => state.buyerOrgDetails[orgUID],
    getSellerOrgDetails: (state) => (orgUID) => state.sellerOrgDetails[orgUID],
    getBuyerOrgTotalCoins: (state) => (orgUID) =>
      state.buyerOrgDetails[orgUID]?.totalOrgCoins,
    getSellerOrgTotalCoins: (state) => (orgUID) =>
      state.sellerOrgDetails[orgUID]?.totalOrgCoins,
    buyerOrgUID: (state) => state.buyerOrgUID,
    sellerOrgUID: (state) => state.sellerOrgUID,
    currentUserOrgUID: (state, _getters, rootState) =>
      rootState.selectedOrgUID || rootState.currentOrgUID || state.currentUserOrgUID,
    currentUserWallet: (state) => state.currentUserWallet,
    currentUserWalletStatus: (state) => state.currentUserWalletStatus,
    privateWalletDetail: (state) => state.privateWalletDetail,
    privateWalletDetailStatus: (state) => state.privateWalletDetailStatus,
    privateWalletStatementHasMore: (state) =>
      state.privateWalletStatementHasMore,
    privateWalletStatementLoadingMore: (state) =>
      state.privateWalletStatementLoadingMore,
    walletBalanceRevision: (state) => state.walletBalanceRevision,
    profileUserData: (state) => state.profileUserData,
    getProfileUserOrgDetails: (state) => () => {
      return state.profileUserOrgDetails || {};
    },
  },
  actions: {
    async fetchProfileUserData({ commit }, userUID) {
      try {
        const userDoc = await getDoc(doc(null, "users", userUID));
        if (userDoc.exists()) {
          const profileUserData = { uid: userDoc.id, ...userDoc.data() };
          commit("SET_PROFILE_USER_DATA", profileUserData);
          return profileUserData;
        } else {
          console.error("No such user!");
          return null;
        }
      } catch (error) {
        console.error("Error fetching user:", error);
        return null;
      }
    },

    async fetchAndSetProfileUserOrgUID({ commit }, userUID) {
      try {
        commit("CLEAR_PROFILE_USER_ORG_DETAILS");

        const unitsRef = collection(null, "users", userUID, "Units");
        const querySnapshot = await getDocs(unitsRef);

        let profileUserOrgUID = null;
        querySnapshot.forEach((docSnap) => {
          if (!profileUserOrgUID) {
            profileUserOrgUID = docSnap.id;
            const data = docSnap.data();

            commit("SET_PROFILE_USER_ORG_DETAILS", {
              orgUID: profileUserOrgUID,
              details: {
                organisationName: data.organisationName,
                roleTitle: data.roleTitle,
                currentRole: data.currentRole,
                organisationUID: data.organisationUID,
                joined: data.joined,
              },
            });
          }
        });

        if (!profileUserOrgUID) {
          console.error("No Units found for user:", userUID);
        }
      } catch (error) {
        console.error("Error fetching and setting profileUserOrgUID:", error);
      }
    },

    async fetchCurrentUserOrgUID({ commit, dispatch, rootGetters }, userUID) {
      if (!userUID) return null;
      const orgUID =
        rootGetters.selectedOrgUID ||
        (await dispatch("ensureSelectedOrg", { userUID }));
      if (orgUID) commit("SET_CURRENT_USER_ORG_UID", orgUID);
      return orgUID || null;
    },

    async fetchCurrentUserWallet({ commit }) {
      if (isOwnWalletDetailFeatureEnabled) {
        commit("SET_CURRENT_USER_WALLET", null);
        commit("SET_CURRENT_USER_WALLET_STATUS", "idle");
        return null;
      }
      assertLegacyMonetaryClientAvailable();
      return null;
    },

    async fetchotherUserData({ commit }, userUID) {
      try {
        const userDoc = await getDoc(doc(null, "users", userUID));
        if (userDoc.exists()) {
          const otherUserData = { uid: userDoc.id, ...userDoc.data() };
          commit("SET_USER_DATA", otherUserData);
        } else {
          console.error("No such user!");
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    },

    async fetchItemOwnerWallet() {
      assertLegacyMonetaryClientAvailable();
      return null;
    },

    async fetchOrgMembersCount() {
      assertLegacyMonetaryClientAvailable();
      return 0;
    },

    startActiveWalletSubscription({ commit }, { uid, orgUID }) {
      if (!uid || !orgUID) return null;
      if (isOwnWalletDetailFeatureEnabled) {
        activeWalletGeneration += 1;
        activeWalletUnsubscribe?.();
        activeWalletUnsubscribe = null;
        activeWalletSubscriptionKey = null;
        commit("SET_CURRENT_USER_WALLET", null);
        commit("SET_CURRENT_USER_ORG_UID", orgUID);
        commit("SET_CURRENT_USER_WALLET_OWNER", uid);
        commit("SET_CURRENT_USER_WALLET_STATUS", "idle");
        return null;
      }
      assertLegacyMonetaryClientAvailable();
      return null;
    },

    stopActiveWalletSubscription({ commit }) {
      activeWalletGeneration += 1;
      privateWalletDetailGeneration += 1;
      activeWalletUnsubscribe?.();
      activeWalletUnsubscribe = null;
      activeWalletSubscriptionKey = null;
      commit("SET_CURRENT_USER_WALLET", null);
      commit("SET_CURRENT_USER_WALLET_OWNER", null);
      commit("SET_CURRENT_USER_WALLET_STATUS", "idle");
      commit("CLEAR_PRIVATE_WALLET_DETAIL", privateWalletDetailGeneration);
    },

    async loadPrivateWalletDetail(
      { state, commit, rootGetters },
      { uid, orgUID, loadMore = false, refresh = false } = {},
    ) {
      if (!isOwnWalletDetailFeatureEnabled) return null;
      const actor = auth.currentUser;
      if (!ownsPrivateWalletContext({ actor, rootGetters, uid, orgUID })) {
        privateWalletDetailGeneration += 1;
        commit("CLEAR_PRIVATE_WALLET_DETAIL", privateWalletDetailGeneration);
        return null;
      }

      let requestCursor = null;
      if (loadMore) {
        if (
          state.privateWalletDetailOwnerUID !== uid ||
          state.privateWalletDetailOrgUID !== orgUID ||
          state.privateWalletDetailStatus !== "ready" ||
          state.privateWalletDetailRefreshing ||
          state.privateWalletStatementLoadingMore ||
          !state.privateWalletStatementHasMore ||
          state.privateWalletStatementCursor === null ||
          state.privateWalletStatementPagesLoaded >=
            MAX_PRIVATE_WALLET_STATEMENT_PAGES
        ) {
          return null;
        }
        requestCursor = state.privateWalletStatementCursor;
      }

      // A refresh reloads the first page behind the visible Wallet (after a
      // payment or Savings move) instead of replacing it with a skeleton.
      const silentRefresh =
        refresh &&
        !loadMore &&
        state.privateWalletDetail !== null &&
        state.privateWalletDetailOwnerUID === uid &&
        state.privateWalletDetailOrgUID === orgUID &&
        state.privateWalletDetailStatus === "ready";

      const generation = ++privateWalletDetailGeneration;
      commit("BEGIN_PRIVATE_WALLET_DETAIL", {
        uid,
        orgUID,
        generation,
        loadMore,
        refresh: silentRefresh,
      });

      try {
        const request = { orgId: orgUID, cursor: requestCursor };
        const result = isWalletStatementV3FeatureEnabled
          ? await walletDetailAuthorityService.getOwnWalletStatement(request)
          : await walletDetailAuthorityService.getOwnWalletDetail(request);
        if (
          !ownsPrivateWalletContext({ actor, rootGetters, uid, orgUID }) ||
          state.privateWalletDetailRequestGeneration !== generation
        ) {
          return null;
        }
        if (
          loadMore &&
          state.privateWalletStatementCursor !== requestCursor
        ) {
          return null;
        }

        const previousRefs = new Set(
          loadMore
            ? statementRowsOf(state.privateWalletDetail).map(statementRowKey)
            : [],
        );
        const overlaps =
          (loadMore &&
            result.schemaVersion !== state.privateWalletDetail?.schemaVersion) ||
          statementRowsOf(result).some((row) =>
            previousRefs.has(statementRowKey(row)),
          );
        const seenCursors = new Set(state.privateWalletStatementSeenCursors);
        if (
          overlaps ||
          (result.nextCursor !== null && seenCursors.has(result.nextCursor))
        ) {
          const error = new Error("The Wallet statement page is inconsistent.");
          error.code = "invalid-response";
          throw error;
        }

        commit("COMPLETE_PRIVATE_WALLET_DETAIL", {
          result,
          loadMore,
          requestCursor,
          generation,
        });
        return result;
      } catch (error) {
        if (
          ownsPrivateWalletContext({ actor, rootGetters, uid, orgUID }) &&
          state.privateWalletDetailRequestGeneration === generation
        ) {
          commit("FAIL_PRIVATE_WALLET_DETAIL", {
            code: privateWalletErrorCode(error),
            loadMore,
            refresh: silentRefresh,
            generation,
          });
        }
        throw error;
      }
    },

    clearPrivateWalletDetail({ commit }) {
      privateWalletDetailGeneration += 1;
      commit("CLEAR_PRIVATE_WALLET_DETAIL", privateWalletDetailGeneration);
    },

    // After a payment, Savings move or expiry tidy-up: forget cached balance
    // summaries, tell balance displays to reload, and refresh the open Wallet.
    async refreshWalletAfterMonetaryChange(
      { state, commit, dispatch },
      { uid, orgUID } = {},
    ) {
      walletSummaryService.clearCache();
      commit("BUMP_WALLET_BALANCE_REVISION");
      if (
        !isOwnWalletDetailFeatureEnabled ||
        !uid ||
        !orgUID ||
        state.privateWalletDetailOwnerUID !== uid ||
        state.privateWalletDetailOrgUID !== orgUID ||
        state.privateWalletDetailStatus !== "ready"
      ) {
        return null;
      }
      try {
        return await dispatch("loadPrivateWalletDetail", {
          uid,
          orgUID,
          refresh: true,
        });
      } catch (_error) {
        // The Wallet stays visible and is marked as possibly out of date.
        return null;
      }
    },

    // Compatibility alias while legacy call sites migrate. It is idempotent
    // and subscribes to one exact wallet rather than the Wallets collection.
    subscribeToWalletUpdates({ dispatch, rootGetters }, uid) {
      const orgUID = rootGetters.selectedOrgUID || rootGetters.currentOrgUID;
      return dispatch("startActiveWalletSubscription", { uid, orgUID });
    },

    async fetchAndCommitOrgMembersCount(
      _context,
      _payload,
    ) {
      assertLegacyMonetaryClientAvailable();
      return null;
    },

    async fetchAndCommitOrgTotalCoins() {
      if (isOwnWalletDetailFeatureEnabled) return null;
      assertLegacyMonetaryClientAvailable();
      return null;
    },

    async fetchUserWalletDetails({ commit, rootGetters }) {
      const orgUID = rootGetters.selectedOrgUID || rootGetters.currentOrgUID;
      if (orgUID) commit("SET_CURRENT_ORG_UID", orgUID);
    },

    async fetchUserOrgCoinSymbol() {
      if (isOwnWalletDetailFeatureEnabled) return null;
      assertLegacyMonetaryClientAvailable();
      return null;
    },

    async fetchAndSetBuyerOrgUID(
      _context,
      _userUID,
    ) {
      assertLegacyMonetaryClientAvailable();
      return null;
    },

    async fetchAndSetSellerOrgUID() {
      assertLegacyMonetaryClientAvailable();
      return null;
    },

    // Moved from misplaced mutations: persist user preferences to Firestore
    async updateUserPreferences(_ctx, { userUid, preferences }) {
      try {
        const userRef = doc(null, "users", userUid);
        await updateDoc(userRef, {
          preferences: preferences,
          updatedAt: new Date().toISOString(),
        });
      } catch (error) {
        console.error("Error updating user preferences:", error);
        throw error;
      }
    },

    async getUserPreferences(_ctx, userUid) {
      try {
        const userDoc = await getDoc(doc(null, "users", userUid));
        if (userDoc.exists()) {
          return userDoc.data().preferences || {};
        }
        return {};
      } catch (error) {
        console.error("Error fetching user preferences:", error);
        return {};
      }
    },
  },
  mutations: {
    SET_CURRENT_USER_ORG_UID(state, orgUID) {
      state.currentUserOrgUID = orgUID;
    },
    // Alias used elsewhere
    SET_CURRENT_ORG_UID(state, orgUID) {
      state.currentUserOrgUID = orgUID;
    },

    SET_CURRENT_USER_WALLET(state, walletData) {
      state.currentUserWallet = walletData;
    },
    SET_CURRENT_USER_WALLET_OWNER(state, uid) {
      state.currentUserWalletOwnerUID = uid;
    },
    SET_CURRENT_USER_WALLET_STATUS(state, status) {
      state.currentUserWalletStatus = status;
    },
    BEGIN_PRIVATE_WALLET_DETAIL(
      state,
      { uid, orgUID, generation, loadMore, refresh = false },
    ) {
      state.privateWalletDetailRequestGeneration = generation;
      state.privateWalletDetailError = null;
      state.privateWalletStatementError = null;
      if (loadMore) {
        state.privateWalletStatementLoadingMore = true;
        return;
      }
      state.privateWalletDetailRefreshing = refresh;
      if (refresh) {
        state.privateWalletStatementLoadingMore = false;
        return;
      }
      state.privateWalletDetailStale = false;
      state.privateWalletDetail = null;
      state.privateWalletDetailOwnerUID = uid;
      state.privateWalletDetailOrgUID = orgUID;
      state.privateWalletDetailStatus = "pending";
      state.privateWalletStatementCursor = null;
      state.privateWalletStatementHasMore = false;
      state.privateWalletStatementLoadingMore = false;
      state.privateWalletStatementPagesLoaded = 0;
      state.privateWalletStatementSeenCursors = [];
      state.privateWalletStatementTruncated = false;
    },
    COMPLETE_PRIVATE_WALLET_DETAIL(
      state,
      { result, loadMore, requestCursor, generation },
    ) {
      if (state.privateWalletDetailRequestGeneration !== generation) return;
      const previousRows = loadMore
        ? statementRowsOf(state.privateWalletDetail)
        : [];
      const pagesLoaded = loadMore
        ? state.privateWalletStatementPagesLoaded + 1
        : 1;
      state.privateWalletDetail = Object.freeze({
        ...result,
        [statementRowsProperty(result)]: Object.freeze([
          ...previousRows,
          ...statementRowsOf(result),
        ]),
      });
      state.privateWalletDetailStatus = "ready";
      state.privateWalletDetailError = null;
      state.privateWalletDetailRefreshing = false;
      state.privateWalletDetailStale = false;
      state.privateWalletStatementLoadingMore = false;
      state.privateWalletStatementError = null;
      state.privateWalletStatementPagesLoaded = pagesLoaded;
      state.privateWalletStatementSeenCursors = [
        ...(loadMore ? state.privateWalletStatementSeenCursors : []),
        ...(requestCursor === null ? [] : [requestCursor]),
      ];
      state.privateWalletStatementTruncated =
        result.hasMore && pagesLoaded >= MAX_PRIVATE_WALLET_STATEMENT_PAGES;
      state.privateWalletStatementHasMore =
        result.hasMore && pagesLoaded < MAX_PRIVATE_WALLET_STATEMENT_PAGES;
      state.privateWalletStatementCursor = state.privateWalletStatementHasMore
        ? result.nextCursor
        : null;
    },
    FAIL_PRIVATE_WALLET_DETAIL(
      state,
      { code, loadMore, refresh = false, generation },
    ) {
      if (state.privateWalletDetailRequestGeneration !== generation) return;
      if (loadMore) {
        state.privateWalletStatementLoadingMore = false;
        state.privateWalletStatementError = code;
        return;
      }
      state.privateWalletDetailRefreshing = false;
      if (refresh) {
        // Keep the last loaded Wallet on screen, flagged as possibly stale.
        state.privateWalletDetailStale = true;
        return;
      }
      state.privateWalletDetailStale = false;
      state.privateWalletDetail = null;
      state.privateWalletDetailStatus = "error";
      state.privateWalletDetailError = code;
      state.privateWalletStatementCursor = null;
      state.privateWalletStatementHasMore = false;
      state.privateWalletStatementLoadingMore = false;
      state.privateWalletStatementPagesLoaded = 0;
      state.privateWalletStatementSeenCursors = [];
      state.privateWalletStatementTruncated = false;
    },
    CLEAR_PRIVATE_WALLET_DETAIL(state, generation) {
      state.privateWalletDetail = null;
      state.privateWalletDetailOwnerUID = null;
      state.privateWalletDetailOrgUID = null;
      state.privateWalletDetailStatus = "idle";
      state.privateWalletDetailError = null;
      state.privateWalletDetailRequestGeneration = generation;
      state.privateWalletDetailRefreshing = false;
      state.privateWalletDetailStale = false;
      state.privateWalletStatementCursor = null;
      state.privateWalletStatementHasMore = false;
      state.privateWalletStatementLoadingMore = false;
      state.privateWalletStatementError = null;
      state.privateWalletStatementPagesLoaded = 0;
      state.privateWalletStatementSeenCursors = [];
      state.privateWalletStatementTruncated = false;
    },

    BUMP_WALLET_BALANCE_REVISION(state) {
      state.walletBalanceRevision += 1;
    },

    // Mutations for user data
    SET_USER_DATA(state, otherUserData) {
      state.otherUserData = otherUserData;
    },
    SET_OTHER_USER_DATA(state, userData) {
      state.otherUserData = userData;
    },

    // Generic org details helpers
    SET_ORG_DETAILS(state, { orgUID, details }) {
      if (!state.orgDetails[orgUID]) {
        state.orgDetails[orgUID] = {};
      }
      state.orgDetails[orgUID] = { ...state.orgDetails[orgUID], ...details };
    },
    SET_MEMBERS_COUNT(state, { orgUID, membersCount }) {
      if (!state.orgDetails[orgUID]) {
        state.orgDetails[orgUID] = { membersCount: 1, totalOrgCoins: 0 };
      }
      state.orgDetails[orgUID].membersCount = membersCount;
    },
    SET_TOTAL_ORG_COINS(state, { orgUID, totalOrgCoins }) {
      if (!state.orgDetails[orgUID]) {
        state.orgDetails[orgUID] = { membersCount: 0, totalOrgCoins: 0 };
      }
      state.orgDetails[orgUID].totalOrgCoins = totalOrgCoins;
    },

    // Coin
    SET_COIN_NAME(state, coinName) {
      state.coinName = coinName;
    },
    SET_COIN_SYMBOL(state, coinSymbol) {
      state.coinSymbol = coinSymbol;
    },

    // Buyer/Seller org details
    SET_BUYER_MEMBERS_COUNT(state, { orgUID, membersCount }) {
      state.buyerOrgDetails[orgUID] = {
        ...state.buyerOrgDetails[orgUID],
        membersCount,
      };
    },
    SET_SELLER_MEMBERS_COUNT(state, { orgUID, membersCount }) {
      state.sellerOrgDetails[orgUID] = {
        ...state.sellerOrgDetails[orgUID],
        membersCount,
      };
    },
    SET_BUYER_TOTAL_ORG_COINS(state, { orgUID, totalOrgCoins }) {
      if (!state.buyerOrgDetails[orgUID]) {
        state.buyerOrgDetails[orgUID] = {};
      }
      state.buyerOrgDetails[orgUID].totalOrgCoins = totalOrgCoins;
    },
    SET_SELLER_TOTAL_ORG_COINS(state, { orgUID, totalOrgCoins }) {
      if (!state.sellerOrgDetails[orgUID]) {
        state.sellerOrgDetails[orgUID] = {};
      }
      // Keep existing fallback behavior
      if (totalOrgCoins === 0) totalOrgCoins = 1;
      state.sellerOrgDetails[orgUID].totalOrgCoins = totalOrgCoins;
    },
    SET_BUYER_ORG_UID(state, orgUID) {
      state.buyerOrgUID = orgUID;
    },
    SET_SELLER_ORG_UID(state, orgUID) {
      state.sellerOrgUID = orgUID;
    },
    SET_BUYER_ORG_DETAILS(state, { orgUID, details }) {
      state.buyerOrgDetails[orgUID] = {
        ...state.buyerOrgDetails[orgUID],
        ...details,
      };
    },
    SET_SELLER_ORG_DETAILS(state, { orgUID, details }) {
      state.sellerOrgDetails[orgUID] = {
        ...state.sellerOrgDetails[orgUID],
        ...details,
      };
    },

    // Profile user data
    SET_PROFILE_USER_DATA(state, profileUserData) {
      state.profileUserData = profileUserData;
    },
    SET_PROFILE_USER_ORG_DETAILS(state, { orgUID, details }) {
      state.profileUserOrgDetails[orgUID] = {
        ...details,
        organisationUID: orgUID,
      };
    },
    CLEAR_PROFILE_USER_ORG_DETAILS(state) {
      state.profileUserOrgDetails = {};
    },
  },
};
